import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import type { ContractLegalStatus } from '@bloomays-lib/types.shared';
import {
  DocumentStatusFilter,
  LineTypeFilter,
  ContractVoletFilter,
  TemplateContract,
} from '@bloomays-lib/types.shared';

/**
 * Étapes (`step`) qui identifient une ligne de contrat PRINCIPAL (par opposition à un avenant).
 * Côté serveur, les avenants sont créés avec `step = "Avenant N"` (ou legacy `"Prolongation N"`),
 * tandis que le contrat principal porte `"Contrat principal"` ou `"CGV"`.
 */
export const MAIN_CONTRACT_STEPS = ['Contrat principal', 'CGV'] as const;

/**
 * Vrai si le `step` correspond à une ligne de contrat principal (et non à un avenant).
 * Centralise la règle historiquement dupliquée (`missions.ts`, frontend `LegalContractFile`).
 */
export const isMainContractStep = (step?: string | null): boolean =>
  MAIN_CONTRACT_STEPS.includes((step ?? '') as (typeof MAIN_CONTRACT_STEPS)[number]);

/**
 * Vrai si le `step` correspond à un AVENANT (extension numérotée), par opposition au contrat
 * principal. Couvre la nomenclature courante `"Avenant N"` et le legacy `"Prolongation N"`.
 * Contrepartie de {@link isMainContractStep} : source unique de la distinction principal ↔ avenant
 * pour toute la base (fiche mission `getPrincipalContract`, `LegalContractFile`, dérivations de statut).
 */
export const isAmendmentStep = (step?: string | null): boolean => /avenant|prolongation/i.test(step ?? '');

/** Champs d'un contrat nécessaires pour dériver son état de signature. */
export type ContractSignatureInput = {
  signedDate?: string | null;
  signedFileUrl?: string | null;
  nbSigner?: number | null;
  signedCount?: number | null;
};

/** Champs d'un contrat nécessaires pour dériver son statut « en retard ». */
export type ContractLateInput = ContractSignatureInput & {
  step?: string | null;
  status?: ContractLegalStatus | null;
};

/**
 * Vrai si le contrat est considéré comme signé : présence d'une date de signature, d'un
 * fichier signé, ou signature complète de tous les signataires. Règle reprise à l'identique
 * de la fiche mission (`contractStates` dans `MissionDashboardV2`) pour éviter toute divergence.
 */
export const isContractSigned = (contract: ContractSignatureInput): boolean =>
  Boolean(contract.signedDate) ||
  Boolean(contract.signedFileUrl) ||
  ((contract.nbSigner ?? 0) > 0 && (contract.signedCount ?? 0) >= (contract.nbSigner ?? 0));

/**
 * Vrai si la mission a démarré (date de début atteinte ou dépassée) à la `referenceDate`.
 * Aligné sur `missionStarted` de la fiche mission : `differenceInCalendarDays(beginMission, now) <= 0`.
 * Renvoie `false` si la date est absente ou invalide (pas de référentiel de retard exploitable).
 */
export const isMissionStarted = (beginMission?: string | Date | null, referenceDate: Date = new Date()): boolean => {
  if (!beginMission) return false;
  const begin = new Date(beginMission);
  if (Number.isNaN(begin.getTime())) return false;
  return differenceInCalendarDays(begin, referenceDate) <= 0;
};

/**
 * Statut dérivé « en retard » d'un contrat : vrai ssi c'est un contrat PRINCIPAL (jamais un
 * avenant), non signé, non annulé/refusé, ET dont la mission a démarré. Règle unique partagée
 * par la fiche mission et la vue « Contrats et avenants » (spec 004, TEC-3161).
 */
export const deriveContractLateStatus = (
  contract: ContractLateInput,
  beginMission?: string | Date | null,
  referenceDate?: Date,
): boolean => {
  if (!isMainContractStep(contract.step)) return false;
  if (contract.status === 'cancelled' || contract.status === 'refused') return false;
  if (isContractSigned(contract)) return false;
  return isMissionStarted(beginMission, referenceDate);
};

/** Compteurs contractuels d'une mission (à signer / en retard / total). */
export type MissionContractCounts = {
  /** Documents à signer (`status === 'on going'`), contrat principal ET avenants. */
  toSign: number;
  /** Contrats principaux en retard (jamais d'avenant). */
  late: number;
  /** Nombre total de documents contractuels de la mission. */
  total: number;
};

/**
 * Agrège les compteurs d'une mission à partir de sa liste plate de contrats (volets SST/PDS,
 * principal + avenants). Le statut `pending` (« erreur » de génération) n'entre pas dans `toSign`
 * (seul `on going` compte). Calcul pur, sans I/O — appelé à la volée par les resolvers.
 */
export const computeMissionContractCounts = (
  contracts: ReadonlyArray<ContractLateInput>,
  beginMission?: string | Date | null,
  referenceDate?: Date,
): MissionContractCounts => ({
  toSign: contracts.filter((contract) => contract.status === 'on going').length,
  late: contracts.filter((contract) => deriveContractLateStatus(contract, beginMission, referenceDate)).length,
  total: contracts.length,
});

/** Synthèse globale toutes missions confondues (bandeau d'alerte). */
export type GlobalContractSummary = {
  toSign: number;
  late: number;
  /**
   * Nombre de missions « concernées » par le bandeau : au moins un document à signer OU en retard.
   * Agrégat global calculé côté serveur (robuste à un futur filtre/pagination serveur, où le front
   * ne recevrait qu'une page de missions et ne pourrait pas le recompter fidèlement).
   */
  missionsConcerned: number;
};

/**
 * Agrège les compteurs par mission en une synthèse globale. Par construction :
 * `summary.toSign === Σ counts.toSign` et `summary.late === Σ counts.late` (cohérence bandeau↔mission).
 */
export const computeGlobalContractSummary = (
  missionCounts: ReadonlyArray<MissionContractCounts>,
): GlobalContractSummary => ({
  toSign: missionCounts.reduce((sum, counts) => sum + counts.toSign, 0),
  late: missionCounts.reduce((sum, counts) => sum + counts.late, 0),
  missionsConcerned: missionCounts.filter((counts) => counts.toSign > 0 || counts.late > 0).length,
});

export const matchDocumentStatus = (
  contract: ContractLateInput,
  statuses?: ReadonlyArray<DocumentStatusFilter> | null,
  beginMission?: string | Date | null,
  referenceDate?: Date,
): boolean => {
  if (!statuses || statuses.length === 0) return true;
  return statuses.some((filter) => {
    switch (filter) {
      case DocumentStatusFilter.toSign:
        return contract.status === 'on going';
      case DocumentStatusFilter.signed:
        return contract.status === 'done';
      case DocumentStatusFilter.pending:
        return contract.status === 'pending';
      case DocumentStatusFilter.refused:
        return contract.status === 'refused';
      case DocumentStatusFilter.cancelled:
        return contract.status === 'cancelled';
      case DocumentStatusFilter.late:
        return deriveContractLateStatus(contract, beginMission, referenceDate);
      default:
        return false;
    }
  });
};

export const matchLineType = (step?: string | null, lineType?: LineTypeFilter | null): boolean => {
  if (!lineType || lineType === LineTypeFilter.all) return true;
  if (lineType === LineTypeFilter.principal) return isMainContractStep(step);
  return isAmendmentStep(step);
};

export const matchVolet = (
  contract: { type: TemplateContract },
  volets?: ReadonlyArray<ContractVoletFilter> | null,
): boolean => {
  if (!volets || volets.length === 0) return true;
  return (volets as ReadonlyArray<string>).includes(contract.type);
};

export type ContractSegment = 'contractSst' | 'contractPds' | 'amendmentSst' | 'amendmentPds';

export type ContractSegmentInput = ContractLateInput & { type: TemplateContract };

export type ContractSegmentCounts = {
  toSign: number;
  late: number;
};

export type MissionContractSegments = Record<ContractSegment, ContractSegmentCounts>;

const SEGMENT_DEFINITIONS: Record<ContractSegment, { lineType: LineTypeFilter; volet: TemplateContract }> = {
  contractSst: { lineType: LineTypeFilter.principal, volet: TemplateContract.SST },
  contractPds: { lineType: LineTypeFilter.principal, volet: TemplateContract.PDS },
  amendmentSst: { lineType: LineTypeFilter.avenant, volet: TemplateContract.SST },
  amendmentPds: { lineType: LineTypeFilter.avenant, volet: TemplateContract.PDS },
};

const CONTRACT_SEGMENTS = Object.keys(SEGMENT_DEFINITIONS) as ContractSegment[];

export const computeMissionContractSegmentCounts = (
  contracts: ReadonlyArray<ContractSegmentInput>,
  beginMission?: string | Date | null,
  referenceDate?: Date,
): MissionContractSegments => {
  const result = {} as MissionContractSegments;
  for (const segment of CONTRACT_SEGMENTS) {
    const { lineType, volet } = SEGMENT_DEFINITIONS[segment];
    const segmentContracts = contracts.filter(
      (contract) => matchLineType(contract.step, lineType) && contract.type === volet,
    );
    result[segment] = {
      toSign: segmentContracts.filter((contract) => contract.status === 'on going').length,
      late: segmentContracts.filter((contract) => deriveContractLateStatus(contract, beginMission, referenceDate))
        .length,
    };
  }
  return result;
};

export type GlobalContractSegmentSummary = ContractSegmentCounts & {
  missionsConcerned: number;
};

export type GlobalContractSegmentsSummary = Record<ContractSegment, GlobalContractSegmentSummary>;

export const computeGlobalContractSegmentSummary = (
  missionSegments: ReadonlyArray<MissionContractSegments>,
): GlobalContractSegmentsSummary => {
  const result = {} as GlobalContractSegmentsSummary;
  for (const segment of CONTRACT_SEGMENTS) {
    result[segment] = {
      toSign: missionSegments.reduce((sum, counts) => sum + counts[segment].toSign, 0),
      late: missionSegments.reduce((sum, counts) => sum + counts[segment].late, 0),
      missionsConcerned: missionSegments.filter((counts) => counts[segment].toSign > 0).length,
    };
  }
  return result;
};
